import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Crown, Check, Loader2, Tag, Shield } from "lucide-react";

const STATUS_LABEL = {
  trialing: "Free Trial Active",
  active: "Subscribed",
  past_due: "Payment Failed — Update Card",
  canceled: "Canceled",
  none: "No Active Plan",
};

// The GM Plan pricing + manage-billing content — lives at /billing on its own, and is reused
// as the "Subscription" tab inside Settings so it has a real home there too.
export function BillingPanel() {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(null); // "monthly" | "annual" | "raid_pass" | "portal" | null
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, percent_off, duration }

  const load = async () => {
    const { data } = await api.get("/billing/me");
    setBilling(data);
  };

  useEffect(() => {
    load();
  }, []);

  const applyPromo = async () => {
    if (!promoInput.trim()) return toast.error("Type a promo code first");
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/payments/validate-promo", { code: promoInput.trim() });
      setAppliedPromo(data);
      toast.success(`${data.percent_off}% off applied!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "That code isn't valid");
      setAppliedPromo(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
  };

  const discountedPrice = (base) => {
    if (!appliedPromo) return null;
    const val = base * (1 - appliedPromo.percent_off / 100);
    return val % 1 === 0 ? `$${val}` : `$${val.toFixed(2)}`;
  };

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan, origin_url: window.location.origin, promo_code: appliedPromo?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setBusy(null);
    }
  };

  if (!billing) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const hasAccess = billing.has_gm_entitlement;

  return (
    <div data-testid="billing-panel">
      <div className="mb-10">
        <h1 className="font-display text-4xl mb-2 flex items-center gap-3"><Crown className="w-8 h-8 text-[#D4AF37]" /> GM Plan</h1>
        <p className="text-sm text-[#f1e0c5]/50">Players always play for free. Being the GM — creating and running your own tables — needs a plan.</p>
        <p className="text-xs text-[#D4AF37] mt-2" data-testid="billing-current-status">Status: {STATUS_LABEL[billing.subscription_status] || billing.subscription_status}
          {billing.one_time_access_until && new Date(billing.one_time_access_until) > new Date() && ` · One-month pass active until ${new Date(billing.one_time_access_until).toLocaleDateString()}`}
          {billing.raid_passes_available > 0 && ` · ${billing.raid_passes_available} Raid Pass${billing.raid_passes_available > 1 ? "es" : ""} available`}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 rounded-2xl bg-black/40 border border-[#D4AF37]/30 flex flex-col" data-testid="billing-plan-monthly">
          <h2 className="font-display text-2xl mb-1">Monthly</h2>
          {appliedPromo ? (
            <p className="text-3xl font-display text-[#4d7c0f] mb-1" data-testid="billing-monthly-discounted-price">
              {discountedPrice(5)}<span className="text-sm text-[#f1e0c5]/40">/mo</span>{" "}
              <span className="text-lg text-[#f1e0c5]/30 line-through font-normal">$5</span>
            </p>
          ) : (
            <p className="text-3xl font-display text-[#D4AF37] mb-1">$5<span className="text-sm text-[#f1e0c5]/40">/mo</span></p>
          )}
          <p className="text-xs text-[#f1e0c5]/50 mb-4">
            {appliedPromo
              ? appliedPromo.duration === "forever"
                ? `${appliedPromo.percent_off}% off applied for as long as you stay subscribed.`
                : `${appliedPromo.percent_off}% off your first month, then $5/mo after.`
              : "7-day free trial, cancel anytime before it ends and you won't be charged. Only want one month? Just cancel after — no separate one-time plan needed."}
          </p>
          <ul className="text-sm space-y-1.5 mb-6 flex-1">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Create unlimited game rooms</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> All GM tools, maps, and rules</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Cancel anytime</li>
          </ul>
          <Button data-testid="billing-subscribe-monthly-button" disabled={!!busy} onClick={() => subscribe("monthly")} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">
            {busy === "monthly" ? "Redirecting…" : "Start 7-Day Free Trial"}
          </Button>
        </div>

        <div className="p-6 rounded-2xl bg-black/40 border border-[#4d7c0f]/50 flex flex-col relative" data-testid="billing-plan-annual">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-[#4d7c0f] text-white">Save 2 Months</span>
          <h2 className="font-display text-2xl mb-1">Annual</h2>
          {appliedPromo ? (
            <p className="text-3xl font-display text-[#4d7c0f] mb-1" data-testid="billing-annual-discounted-price">
              {discountedPrice(50)}<span className="text-sm text-[#f1e0c5]/40">/yr</span>{" "}
              <span className="text-lg text-[#f1e0c5]/30 line-through font-normal">$50</span>
            </p>
          ) : (
            <p className="text-3xl font-display text-[#4d7c0f] mb-1">$50<span className="text-sm text-[#f1e0c5]/40">/yr</span></p>
          )}
          <p className="text-xs text-[#f1e0c5]/50 mb-4">
            {appliedPromo
              ? appliedPromo.duration === "forever"
                ? `${appliedPromo.percent_off}% off applied for as long as you stay subscribed.`
                : `${appliedPromo.percent_off}% off your first payment, then $50/yr after.`
              : "Same 7-day free trial. Works out to $4.17/mo — 2 months cheaper than paying monthly all year."}
          </p>
          <ul className="text-sm space-y-1.5 mb-6 flex-1">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Everything in Monthly</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Billed once a year — set it and forget it</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Cancel anytime</li>
          </ul>
          <Button data-testid="billing-subscribe-annual-button" disabled={!!busy} onClick={() => subscribe("annual")} className="w-full rounded-full bg-[#4d7c0f] hover:bg-[#3d6208]">
            {busy === "annual" ? "Redirecting…" : "Start 7-Day Free Trial"}
          </Button>
        </div>

        <div className="p-6 rounded-2xl bg-black/40 border border-[#1E3A8A]/40 flex flex-col" data-testid="billing-plan-raid-pass">
          <h2 className="font-display text-2xl mb-1 flex items-center gap-2"><Shield className="w-5 h-5 text-[#3B82F6]" /> Raid Pass</h2>
          {appliedPromo ? (
            <p className="text-3xl font-display text-[#4d7c0f] mb-1" data-testid="billing-raid-pass-discounted-price">
              {discountedPrice(5)}<span className="text-sm text-[#f1e0c5]/40"> one-time</span>{" "}
              <span className="text-lg text-[#f1e0c5]/30 line-through font-normal">$5</span>
            </p>
          ) : (
            <p className="text-3xl font-display text-[#f1e0c5] mb-1">$5<span className="text-sm text-[#f1e0c5]/40"> one-time</span></p>
          )}
          <p className="text-xs text-[#f1e0c5]/50 mb-4">
            With an active GM Pass (one-time or recurring), starting a Raid is already free and lets in as many Generals as you want — as long as THEY also have their own GM Pass/trial. A Raid Pass is only needed to let non-Pass Generals in — one Pass fills your table with up to 10 of them, for any Raid you run.
          </p>
          <ul className="text-sm space-y-1.5 mb-6 flex-1">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Covers up to 10 non-Pass Generals, for any Raid</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> No recurring charge — pay again only once you need more than 10</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#4d7c0f]" /> Split the cost with your table if you want</li>
          </ul>
          {billing.has_gm_entitlement ? (
            <Button data-testid="billing-subscribe-raid-pass-button" disabled={!!busy} variant="secondary" onClick={() => subscribe("raid_pass")} className="w-full rounded-full">
              {busy === "raid_pass" ? "Redirecting…" : discountedPrice(5) ? `Pay ${discountedPrice(5)} for a Raid` : "Pay $5 for a Raid"}
            </Button>
          ) : (
            <p className="text-xs text-[#f1e0c5]/40 italic" data-testid="billing-raid-pass-needs-subscription">Get any GM Pass (Monthly, Annual, or one-time) first to unlock Raid Passes.</p>
          )}
        </div>
      </div>

      <div className="mt-6 text-center">
        {appliedPromo ? (
          <div className="inline-flex items-center gap-2 bg-[#4d7c0f]/15 border border-[#4d7c0f]/40 rounded-full px-4 py-2 text-sm" data-testid="billing-promo-applied">
            <Tag className="w-4 h-4 text-[#4d7c0f]" />
            <span><span className="uppercase font-semibold">{appliedPromo.code}</span> — {appliedPromo.percent_off}% off {appliedPromo.duration === "forever" ? "for the life of your plan" : "your first payment"} will apply at checkout</span>
            <button data-testid="billing-promo-clear-button" onClick={clearPromo} className="text-[#f1e0c5]/40 hover:text-[#D90429] ml-1">✕</button>
          </div>
        ) : showPromoInput ? (
          <div className="inline-flex items-center gap-2" data-testid="billing-promo-input-row">
            <Input
              data-testid="billing-promo-input"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyPromo()}
              placeholder="Enter promo code"
              className="w-48 bg-black/30 border-white/10 uppercase"
            />
            <Button data-testid="billing-promo-apply-button" size="sm" variant="secondary" className="rounded-full" onClick={applyPromo} disabled={applyingPromo}>
              {applyingPromo ? "Checking…" : "Apply"}
            </Button>
          </div>
        ) : (
          <button data-testid="billing-promo-toggle-button" onClick={() => setShowPromoInput(true)} className="text-sm text-[#f1e0c5]/50 underline hover:text-[#D4AF37]">
            Have a promo code?
          </button>
        )}
      </div>

      {billing.has_billing_account && (
        <div className="mt-8 text-center">
          <button data-testid="billing-manage-button" onClick={openPortal} disabled={!!busy} className="text-sm text-[#f1e0c5]/50 underline hover:text-[#D4AF37]">
            {busy === "portal" ? "Opening…" : "Manage billing / cancel subscription"}
          </button>
        </div>
      )}

      {hasAccess && (
        <p className="mt-6 text-center text-xs text-[#4d7c0f]" data-testid="billing-active-note">You're all set — head back to your dashboard to create a game.</p>
      )}
    </div>
  );
}
